import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { 
  getUserWatchlist, 
  addToWatchlist, 
  removeFromWatchlist 
} from '../services/websocketApi/watchlist';

const WatchlistContext = createContext();

export const WatchlistProvider = ({ children }) => {
  const [watchlist, setWatchlist] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Use refs to track ongoing requests and prevent duplicates
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const fetchTimeoutRef = useRef(null);
  const DEBOUNCE_TIME = 2000; // 2 seconds debounce

  // Fetch watchlist data from API with debouncing to prevent multiple rapid calls
  const fetchWatchlist = async () => {
    // Return immediately if already loading
    if (isFetchingRef.current) {
      console.log('Watchlist fetch already in progress, skipping duplicate call');
      return watchlist;
    }
    
    // Check if we've fetched recently (within debounce time)
    const now = Date.now();
    if (now - lastFetchTimeRef.current < DEBOUNCE_TIME) {
      console.log('Watchlist fetched recently, debouncing request');
      
      // Clear any existing timeout
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      
      // Create a promise that will resolve with current watchlist after timeout
      return new Promise(resolve => {
        fetchTimeoutRef.current = setTimeout(() => {
          fetchWatchlist().then(resolve);
        }, DEBOUNCE_TIME - (now - lastFetchTimeRef.current));
      });
    }
    
    // Proceed with the fetch
    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Fetching watchlist from context...');
      const data = await getUserWatchlist();
      console.log('Watchlist data received in context:', data);
      setWatchlist(data || []);
      lastFetchTimeRef.current = Date.now();
      return data;
    } catch (err) {
      console.error('Error fetching watchlist in context:', err);
      setError(err.message);
      return watchlist;
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Add instrument to watchlist
  const addInstrument = async (symbol) => {
    if (!symbol) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      await addToWatchlist(symbol);
      // After adding, refresh watchlist to get updated data
      await fetchWatchlist();
      return true;
    } catch (err) {
      console.error(`Error adding ${symbol} to watchlist:`, err);
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Remove instrument from watchlist
  const removeInstrument = async (symbol) => {
    if (!symbol) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      await removeFromWatchlist(symbol);
      // After removing, refresh watchlist to get updated data
      await fetchWatchlist();
      return true;
    } catch (err) {
      console.error(`Error removing ${symbol} from watchlist:`, err);
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Check if an instrument is in the watchlist
  const isInWatchlist = (instrumentId) => {
    // Return false if instrumentId is null/undefined or watchlist is empty
    if (!instrumentId || !watchlist || !watchlist.length) {
      return false;
    }

    // Check if the instrument is in the watchlist
    return watchlist.some(item => {
      // Ensure the item itself is not null and has an instrumentId
      return item && item.instrumentId && item.instrumentId === instrumentId;
    });
  };

  // Toggle instrument in watchlist
  const toggleInstrument = async (instrumentId) => {
    if (isInWatchlist(instrumentId)) {
      return await removeInstrument(instrumentId);
    } else {
      return await addInstrument(instrumentId);
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  // Provide context values
  const value = {
    watchlist,
    isLoading,
    error,
    fetchWatchlist,
    addInstrument,
    removeInstrument,
    isInWatchlist,
    toggleInstrument
  };

  return (
    <WatchlistContext.Provider value={value}>
      {children}
    </WatchlistContext.Provider>
  );
};

// Custom hook to use the watchlist context
export const useWatchlist = () => {
  const context = useContext(WatchlistContext);
  if (!context) {
    throw new Error('useWatchlist must be used within a WatchlistProvider');
  }
  return context;
};

export default WatchlistContext; 